

// 숫자랑, 소수점만 되는 정규식
export const numericRegex = /^(|\d+(\.\d*)?|0\.)$/

// 컴마랑 소수점이랑 같이 계산할때 사용
export const formatComma = (value: string | number, decimalPlaces: number = 2): string => {
  // 입력값 유효성 검사
  if (typeof value !== 'string' && typeof value !== 'number') {
    throw new Error('입력값은 문자열 또는 숫자여야 합니다.')
  }

  // 문자열로 변환 및 음수 처리
  const numberString = typeof value === 'number' ? Math.abs(value).toString() : value.replace('-', '')
  const isNegative = typeof value === 'number' ? value < 0 : value.startsWith('-')

  // 소수점 분리
  const [integerPart, decimalPart] = numberString.split('.')

  // 정수 부분에 콤마 추가
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  // 소수점 이하 자릿수 제한
  const formattedDecimalPart = decimalPart ? '.' + decimalPart.slice(0, decimalPlaces).padEnd(decimalPlaces, '0') : '.'.padEnd(decimalPlaces + 1, '0')

  let result = ''

  // 결과 조합
  if (decimalPlaces === 0) {
    result = `${formattedIntegerPart}`
  } else {
    result = `${formattedIntegerPart}${formattedDecimalPart}`
  }

  return isNegative ? '-' + result : result
}

// Map 에서 colume 컬럼 데이터만 array 뽑는 방법
export function getColumnFromMap<T, K extends keyof T>(map: Map<any, T>, column: K): T[K][] {
  const result: T[K][] = []

  if (isEmpty(map)) {
    return result
  }

  map.forEach((value) => {
    result.push(value[column]) // 각 값의 특정 컬럼을 배열에 추가
  })

  return result
}

export function DecimalPlaces(value: string | number): number {
  let tmpVal: string | number = 0
  if (typeof value === 'number') {
    tmpVal = value
  } else {
    const valueString = value.toString()
    if (valueString.includes('e')) {
      return Math.abs(parseInt(valueString.split('e-')[1], 10))
    }
  }

  // 숫자인 경우 문자열로 변환하여 처리 (지수 표기법 방지)
  const valueStr = typeof tmpVal === 'number' ? tmpVal.toFixed(10) : tmpVal

  if (!String(valueStr).includes('.') || parseFloat(valueStr) === 0) return 0

  return valueStr.split('.')[1].replace(/0+$/, '').length
}

export function formatNumber(value: number, decimalCnt: number): string {
  try {
    // 소수점이 없을 때는 그냥 출력
    if (decimalCnt === 0) return String(Math.floor(value))
    // Ensure value is a number
    const numValue = typeof value === 'number' ? value : Number(value)
    // Use toFixed only when decimal places are needed
    const fixedValue = decimalCnt > 0 ? numValue.toFixed(decimalCnt) : numValue.toString()

    // 소수점 이후 필요한 0은 유지하고, 불필요한 소수점과 0만 제거
    if (decimalCnt > 0) {
      return fixedValue.replace(/(\.\d*?[1-9])0+$/g, '$1').replace(/\.0+$/, '.0')
    }

    return fixedValue
  } catch (e) {
    console.log(e)
    return ''
  }
}

export function ToFixedNumber(value: number, decimalPlaces: number) {
  return parseFloat(Number(value).toFixed(decimalPlaces))
}

export const jsonToMap = (jsonStr: any): Map<any, any> => {
  if (typeof jsonStr === 'string') {
    try {
      const parsedData = JSON.parse(jsonStr)
      return Array.isArray(parsedData) ? new Map(parsedData) : new Map()
    } catch (error) {
      console.error('Error parsing string to Map:', error)
      return new Map()
    }
  }

  // 배열 형식이면 그대로 Map으로 변환
  if (Array.isArray(jsonStr)) {
    return new Map(jsonStr)
  }

  return new Map()
}

export function GetKey(symbol: string, positionSide: string) {
  return `${symbol.toUpperCase()}_${positionSide.toUpperCase()}`
}

export function DivKey(key: string) {
  const tmp = key.split('_')
  return {
    symbol: tmp[0],
    positionSide: tmp[1],
  }
}

//실수의 경우 뒤 0을 제외한 소숫점 자리수를 리턴 (문자로 줘야함)
export const getFloatDotSize = (fData: string) => {
  const l = fData.length
  let dot = false
  let ss = 0
  for (let i = 0; i < l; i++) {
    if (fData[i] === '.') {
      dot = true
    } else if (dot === true && fData[i] !== '0') {
      ss++
      return ss
    } else if (dot === true && fData[i] === '0') {
      ss++
    }
  }
  return 0
}

// 지정 소숫점 하위를 잘라서 리턴 (반올림 없음)
export const ConvetDotSize = (fData: string, dotSize: number): string => {
  if (!fData) return fData

  const arr = String(fData).split('.')

  // dotSize가 0이면 소수점 이하를 표시하지 않고 정수 부분만 반환
  if (dotSize <= 0) {
    return arr[0]
  }

  // 소수점 이하 자릿수가 없는 경우를 대비
  const integerPart = arr[0]
  const decimalPart = arr[1]?.slice(0, dotSize) || ''

  // 소수점 이하를 잘라낸 후, 부족하면 0을 추가하여 자리수 맞춤
  return decimalPart ? `${integerPart}.${decimalPart.padEnd(dotSize, '0')}` : `${integerPart}.${'0'.repeat(dotSize)}`
}

// string -> 앞6자리 노출 + 뒤 6자리 노출 + 중앙 […] 처리
export const formatMasking = (str: string): string => {
  const firstSix = str.slice(0, 6)
  const lastSix = str.slice(-6)
  return `${firstSix}…${lastSix}`
}

// 금액 단위 변환 (버림 처리) - 메인페이지용
export const formatNumberWithUnits = (value: string | number) => {
  if (!value) return ''
  const newValue = Number(value)
  if (newValue >= 1_000_000_000) {
    return Math.floor(newValue / 1_000_000_000 * 100) / 100 + 'B' // 십억 단위로 변환
  } else if (newValue >= 1_000_000) {
    return Math.floor(newValue / 1_000_000 * 100) / 100 + 'M' // 백만 단위로 변환
  } else if (newValue >= 1_000) {
    return Math.floor(newValue / 1_000 * 100) / 100 + 'K' // 천 단위로 변환
  } else {
    return Math.floor(newValue / 1_000 * 100) / 100 + 'K' // 천 단위 미만도 K 단위로 변환 (예: 520 -> 0.52K)
  }
}

// 금액 단위 변환 (버림 처리) - futures용
export const formatNumberWithUnitsFutures = (value: string | number) => {
  if (!value) return ''
  const newValue = Number(value)
  if (newValue >= 1_000_000_000) {
    return Math.floor(newValue / 1_000_000_000 * 100) / 100 + 'B' // 십억 단위로 변환
  } else if (newValue >= 1_000_000) {
    return Math.floor(newValue / 1_000_000 * 100) / 100 + 'M' // 백만 단위로 변환
  } else if (newValue >= 1_000) {
    return Math.floor(newValue / 1_000 * 100) / 100 + 'K' // 천 단위로 변환
  } else {
    return newValue.toString() // 천 단위 미만은 그대로 표시
  }
}


// 상승/하락 변동률 +,- 표시 변환
export const formatPriceChangePercent = (priceChangePercent: string | number) => {
  if (priceChangePercent == null) return '' // 값이 없을 때 처리
  const formattedPercent = Math.round(Number(priceChangePercent) * 100) / 100 // 소수점 셋째자리 반올림

  return formattedPercent >= 0 ? `+ ${formattedPercent.toFixed(2)}` : `- ${Math.abs(formattedPercent).toFixed(2)}` // -1321 => - 1321
}

// ex => ("645353.5000000" ,  0.1) =>  이걸 645353.5
export function formatDecimalByPrecision(value: string, precisionValue: number): string {
  // 문자열을 숫자로 변환
  const floatValue = parseFloat(value)

  const precision = getDecimalPlaces(precisionValue)

  // 소수점 자릿수에 맞춰서 반올림 후 반환
  const formattedValue = floatValue.toFixed(precision)

  // 최종적으로 불필요한 0을 제거하여 숫자로 변환 후 반환
  return formattedValue
}

export function getDecimalPlaces(value: number): number {
  // 소수점 자릿수를 정확히 계산하기 위해 절대값을 문자열로 변환
  const valueStr = value.toFixed(10) // 충분히 큰 자릿수로 변환 (10자리를 기준으로)

  // 소수점 이하 자릿수를 계산
  if (valueStr.indexOf('.') !== -1) {
    const decimalPart = valueStr.split('.')[1]
    // 불필요한 0을 제거한 후 자릿수 계산
    return decimalPart.replace(/0+$/, '').length
  }

  return 0 // 소수점이 없는 경우 0 리턴
}

/**
 * 소수점을 받아서 해당 소수점을 정수로 변환하는 함수 (지수 표기법(e-7) 처리 포함)
 * @param decimal 소수점 값을 문자열로 입력
 * @returns 변환된 정수값
 */
export function decimalToInteger(decimal: string): number {
  if (decimal === '0') return 0

  // e-표기법이 있는 경우 숫자로 변환 후 처리
  let decimalStr = decimal
  if (decimal.includes('e')) {
    // 지수 표기법을 숫자로 변환한 후 다시 문자열로 변환
    decimalStr = Number(decimal).toString()
  }

  // 끝에 불필요한 0을 제거
  decimalStr = decimalStr.replace(/0+$/, '')

  // 소수점이 포함되어 있는지 확인 후, 소수점 자릿수 계산
  const decimalPlaces = decimalStr.includes('.') ? decimalStr.split('.')[1]?.length || 0 : 0

  return Math.pow(10, decimalPlaces)
}

/**
 * 다양한 데이터 구조(Map, Array, Object 등)가 비어있는지 확인하는 함수
 * @param value 체크할 값 (Map, Array, Object 등)
 * @returns 비어있으면 true, 아니면 false
 */
export function isEmpty(value: any): boolean {
  // null 또는 undefined일 경우
  if (value === null || value === undefined) return true

  // 배열인 경우
  if (Array.isArray(value)) return value.length === 0

  // Map인 경우
  if (value instanceof Map) return value.size === 0

  // Object인 경우
  if (typeof value === 'object') return Object.keys(value).length === 0

  // 그 외의 경우 (문자열, 숫자 등)
  return false
}

/**
 * pricescale에 따라 price 값을 변환하는 함수
 * @param price 변환할 가격 값
 * @param pricescale 소수점 자리를 결정하는 pricescale 값
 * @returns 변환된 price 값
 */
export function formatChartPrice(price: number, pricescale: number): number {
  // pricescale에 따라 소수점 자릿수 계산
  const decimalPlaces = Math.log10(pricescale)

  // 소수점 자릿수에 맞춰서 price를 변환
  const formattedPrice = price.toFixed(decimalPlaces)

  // 숫자로 변환 후 반환 (toFixed는 문자열을 반환하므로)
  return parseFloat(formattedPrice)
}

export function roundPriceToPrecision(price: string, precision: string) {
  // 문자열로 된 숫자를 직접 처리하여 정밀도 유지
  const precisionNum = parseFloat(precision)
  const priceNum = parseFloat(price)

  // 정밀도의 역수를 계산 (예: 0.01 -> 100)
  const factor = 1 / precisionNum

  // Math.round를 사용하여 더 정확한 반올림 처리
  const roundedPrice = Math.round(priceNum * factor) / factor

  // precision으로부터 소수점 자릿수를 계산
  // 값이 없으면 자리수 고정
  const precis = !precision ? '0.0001' : precision
  const decimalPlaces = precis.includes('.') ? precis.split('.')[1].length : 0

  return roundedPrice.toFixed(decimalPlaces)
}

// 앞자리 대문자로 변경
export const formatCapitalizeString = (incomeType: string) => {
  return incomeType
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// 검색어 필터링 함수
export const filterByTerm = <T>(list: T[], term: string, getValues: (item: T) => string[]) => {
  return list.filter((el) => {
    const values = getValues(el)
    return values.some((value) => value && value.toLowerCase().includes(term.toLowerCase()))
  })
}

// 옵션 검색 함수 {value , label}
export const filterOptionsByTerm = (options: { value: string; label: string }[], searchTerm: string) => {
  if (!searchTerm) return options // 검색어가 없으면 모든 옵션 반환
  const lowerCaseTerm = searchTerm.toLowerCase() // 대소문자 구분 없이 검색하기 위해 소문자로 변환
  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(lowerCaseTerm) || // label 속성을 소문자로 변환하여 검색
      option.value.toLowerCase().includes(lowerCaseTerm), // value 속성을 소문자로 변환하여 검색
  )
}

// 베이스64 제대로 변환
export function base64Decode(str: string) {
  // 디코드 된 바이트 스트림으로 변환
  const bytes = atob(str)
    .split('')
    .map((ch) => ch.charCodeAt(0))

  // 바이트 스트림에서 UTF-8 문자열로 변환
  let decodedStr = ''
  const byteCount = bytes.length
  for (let i = 0; i < byteCount; i++) {
    const byte1 = bytes[i]
    if (byte1 < 128) {
      decodedStr += String.fromCharCode(byte1)
    } else if (byte1 >= 192 && byte1 < 224) {
      const byte2 = bytes[++i]
      decodedStr += String.fromCharCode(((byte1 & 31) << 6) | (byte2 & 63))
    } else if (byte1 >= 224 && byte1 < 240) {
      const byte2 = bytes[++i]
      const byte3 = bytes[++i]
      decodedStr += String.fromCharCode(((byte1 & 15) << 12) | ((byte2 & 63) << 6) | (byte3 & 63))
    } else if (byte1 >= 240 && byte1 < 248) {
      const byte2 = bytes[++i]
      const byte3 = bytes[++i]
      const byte4 = bytes[++i]
      const charCode = ((byte1 & 7) << 18) | ((byte2 & 63) << 12) | ((byte3 & 63) << 6) | ((byte4 & 63) - 0x10000)
      decodedStr += String.fromCharCode(0xd800 + (charCode >> 10), 0xdc00 + (charCode & 0x3ff))
    }
  }

  return decodedStr
}

// 천단위 콤마 추가
export const formatNumStringAddCommas = (numberString: string): string => {
  // 숫자와 소수점만 남기고 제거
  const cleanNumberString = String(numberString).replace(/[^\d.]/g, '');

  // 유효하지 않은 숫자 문자열이면 원본 반환
  if (!/^\d+(\.\d+)?$/.test(cleanNumberString)) {
    return numberString;
  }

  // 정수와 소수점 부분 분리
  const parts = cleanNumberString.split('.');
  const integerPart = parts[0] || '';
  const decimalPart = parts[1];

  // 정수 부분에 콤마 추가
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // 소수점이 있으면 소수점 부분 추가해서 반환
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};


// 소수점 자릿수 지정 (기본값 2자리)
export const formatNumStringToFixed = (numberString: string, decimal: number = 2): string => {
  // 숫자가 하나라도 있는지 먼저 확인
  if (!/\d/.test(numberString)) {
    return numberString;
  }

  // 숫자와 소수점만 남기고 정리
  const cleanNumberString = numberString.replace(/[^\d.]/g, '');

  // 유효한 숫자가 아니면 원본 반환
  if (!/^\d+(\.\d+)?$/.test(cleanNumberString)) {
    return numberString;
  }

  // 숫자로 변환 후 소수점 고정
  const fixedNumber = Number(cleanNumberString).toFixed(decimal);

  return fixedNumber;
};

// 차트 간격 변환 함수들
export function convIntervalStr(resolution: string): string {
  const upper = resolution.toUpperCase()

  switch (upper) {
    case '1':
      return '1m'
    case '3':
      return '3m'
    case '5':
      return '5m'
    case '15':
      return '15m'
    case '30':
      return '30m'
    case '60':
      return '1h'
    case '120':
      return '2h'
    case '240':
      return '4h'
    case '360':
      return '6h'
    case '480':
      return '8h'
    case '720':
      return '12h'
    case '1D':
      return '1d'
    case '3D':
      return '3d'
    case '1W':
      return '1w'
    case '1M':
      return '1M'
    default:
      return resolution
  }
}


export function convIntervalToResolution(resolution: string): string {
  switch (resolution) {
    case '1m':
      return '1'
    case '3m':
      return '3'
    case '5m':
      return '5'
    case '15m':
      return '15'
    case '30m':
      return '30'
    case '1h':
      return '60'
    case '2h':
      return '120'
    case '4h':
      return '240'
    case '6h':
      return '360'
    case '8h':
      return '480'
    case '12h':
      return '720'
    case '1d':
      return '1D'
    case '3d':
      return '3D'
    case '1w':
      return '1w'
    case '1M':
      return '1M'
    default:
      return '1'
  }
}
